/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const DEFAULT_DEBOUNCE_MS = 1500;
const toastPosition = toast.POSITION.TOP_RIGHT;

/* eslint-disable no-unused-vars -- callback params are type-only in this options interface */
export interface UseDebouncedDesignSaveOptions<T> {
  enabled: boolean;
  debounceMs?: number;
  toastId: string;
  savingMessage: string;
  savedMessage: string;
  errorMessage: string;
  isValid: (value: T) => boolean;
  save: (value: T) => Promise<unknown>;
  serialize?: (value: T) => string;
}
/* eslint-enable no-unused-vars */

export function useDebouncedDesignSave<T>(
  payload: T | null,
  {
    enabled,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    toastId,
    savingMessage,
    savedMessage,
    errorMessage,
    isValid,
    save,
    serialize = JSON.stringify
  }: UseDebouncedDesignSaveOptions<T>
) {
  const payloadKey = payload ? serialize(payload) : null;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPayloadRef = useRef<T | null>(null);
  const lastPersistedRef = useRef<string | null>(null);
  const hasPendingDebounceRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const saveAgainAfterFlightRef = useRef(false);
  const payloadRef = useRef<T | null>(null);

  const optionsRef = useRef({ toastId, savingMessage, savedMessage, errorMessage, isValid, save, serialize });
  optionsRef.current = { toastId, savingMessage, savedMessage, errorMessage, isValid, save, serialize };

  payloadRef.current = payload;

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    hasPendingDebounceRef.current = false;
  }, []);

  const persistPayload = useCallback(async (payloadToSave: T) => {
    const {
      serialize: serializeFn,
      save: saveFn,
      toastId: id,
      savingMessage: saving,
      savedMessage: saved,
      errorMessage: error
    } = optionsRef.current;
    const serialized = serializeFn(payloadToSave);

    if (serialized === lastPersistedRef.current) {
      return;
    }

    if (saveInFlightRef.current) {
      pendingPayloadRef.current = payloadToSave;
      saveAgainAfterFlightRef.current = true;
      return;
    }

    saveInFlightRef.current = true;
    toast.loading(saving, { toastId: id, position: toastPosition });

    try {
      await saveFn(payloadToSave);
      lastPersistedRef.current = serialized;
      toast.update(id, {
        render: saved,
        type: 'success',
        isLoading: false,
        autoClose: 2500,
        position: toastPosition
      });
    } catch {
      toast.update(id, {
        render: error,
        type: 'error',
        isLoading: false,
        autoClose: 4000,
        position: toastPosition
      });
    } finally {
      saveInFlightRef.current = false;

      if (saveAgainAfterFlightRef.current) {
        saveAgainAfterFlightRef.current = false;
        const next = pendingPayloadRef.current;
        if (next && serializeFn(next) !== lastPersistedRef.current) {
          persistPayload(next);
        }
      }
    }
  }, []);

  const flushPendingSave = useCallback(() => {
    const { isValid: isValidFn, serialize: serializeFn } = optionsRef.current;
    clearDebounceTimer();

    const pending = pendingPayloadRef.current;
    if (!pending || !isValidFn(pending)) {
      return;
    }

    if (serializeFn(pending) === lastPersistedRef.current) {
      return;
    }

    persistPayload(pending);
  }, [clearDebounceTimer, persistPayload]);

  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;

  const markPersisted = useCallback(
    (persistedPayload: T) => {
      const { serialize: serializeFn, toastId: id } = optionsRef.current;
      lastPersistedRef.current = serializeFn(persistedPayload);
      pendingPayloadRef.current = persistedPayload;
      clearDebounceTimer();
      toast.dismiss(id);
    },
    [clearDebounceTimer]
  );

  useEffect(() => {
    const { isValid: isValidFn } = optionsRef.current;

    if (!enabled || !payloadKey || !payloadRef.current) {
      return undefined;
    }

    if (!isValidFn(payloadRef.current)) {
      return undefined;
    }

    if (payloadKey === lastPersistedRef.current) {
      clearDebounceTimer();
      return undefined;
    }

    pendingPayloadRef.current = payloadRef.current;
    hasPendingDebounceRef.current = true;
    clearDebounceTimer();

    debounceTimerRef.current = setTimeout(() => {
      hasPendingDebounceRef.current = false;
      debounceTimerRef.current = null;
      if (payloadRef.current) {
        persistPayload(payloadRef.current);
      }
    }, debounceMs);

    return clearDebounceTimer;
  }, [clearDebounceTimer, debounceMs, enabled, payloadKey, persistPayload]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasPendingDebounceRef.current || saveInFlightRef.current) {
        flushPendingSaveRef.current();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushPendingSaveRef.current();
      clearDebounceTimer();
      toast.dismiss(optionsRef.current.toastId);
    };
  }, [clearDebounceTimer]);

  return {
    flushPendingSave,
    markPersisted
  };
}
